import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiFetch, useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { formatPrice, fmtEventDateTime } from "@/lib/format";
import { toast } from "sonner";
import { ChevronLeft, Ticket, MapPin } from "lucide-react";

function statusBadge(b) {
  const rs = (b.refund_status || "").toLowerCase();
  if (rs && rs !== "failed") return { label: "Refunded", variant: "destructive" };
  const s = (b.status || "").toLowerCase();
  if (s.startsWith("cancelled") || s === "failed") return { label: "Cancelled", variant: "destructive" };
  if (s === "pending_payment") return { label: "Payment pending", variant: "secondary" };
  if (s === "confirmed") return { label: "Confirmed", variant: "default" };
  return { label: s || "Unknown", variant: "secondary" };
}

export default function BookingDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { token } = useAuth();
  const [sp] = useSearchParams();
  const [b, setB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelStage, setCancelStage] = useState("idle"); // idle | code
  const [cancelMode, setCancelMode] = useState("cancel");
  const [cancelEmail, setCancelEmail] = useState("");
  const [cancelCode, setCancelCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(sp.get("checkout") || null);

  const load = useCallback(async (poll = false) => {
    const path = poll ? `/bookings/${id}/payment-status` : `/bookings/${id}`;
    const r = await apiFetch(path, { method: "GET" }, token);
    const data = await r.json();
    if (r.ok) {
      setB(data.booking);
      if (data.booking?.stripe_session_url && !checkoutUrl) setCheckoutUrl(data.booking.stripe_session_url);
    }
    setLoading(false);
  }, [id, token, checkoutUrl]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [load]);

  // If payment is pending, poll every 3s for up to 3 minutes.
  useEffect(() => {
    if (!b) return;
    if (b.status !== "pending_payment") return;
    const iv = setInterval(() => load(true), 3000);
    const t = setTimeout(() => clearInterval(iv), 180000);
    return () => { clearInterval(iv); clearTimeout(t); };
  }, [b, load]);

  const openCheckout = () => {
    const url = checkoutUrl || b?.stripe_session_url;
    if (!url) return;
    window.open(url, "_blank");
  };

  const requestCancel = async (mode) => {
    setCancelMode(mode);
    setBusy(true);
    try {
      const r = await apiFetch(`/bookings/${id}/cancel/request`, {
        method: "POST", body: JSON.stringify({ mode }),
      }, token);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d?.detail || "Cancellation failed"); return; }
      if (d?.requires_otp === false) {
        // Admin: cancel directly
        const r2 = await apiFetch(`/bookings/${id}/cancel`, {
          method: "POST", body: JSON.stringify({ mode }),
        }, token);
        if (r2.ok) { toast.success("The booking has been cancelled."); load(); }
        else { const d2 = await r2.json().catch(() => ({})); toast.error(d2?.detail || "Cancellation failed"); }
        return;
      }
      setCancelEmail(d?.email || "");
      setCancelStage("code");
      setCancelCode("");
    } finally { setBusy(false); }
  };

  const submitCancel = async () => {
    if (cancelCode.trim().length !== 6) return toast.error("Enter the 6-digit code from your email");
    setBusy(true);
    try {
      const r = await apiFetch(`/bookings/${id}/cancel`, {
        method: "POST", body: JSON.stringify({ code: cancelCode.trim(), mode: cancelMode }),
      }, token);
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { toast.error(d?.detail || "Please try again"); return; }
      setCancelStage("idle"); setCancelCode("");
      if (cancelMode === "not_attending") toast.success("Marked as not attending — organiser notified.");
      else if (d?.refund?.ok) toast.success(`Booking cancelled. Refund will hit your card in a few business days.`);
      else toast.success("Booking cancelled.");
      load();
    } finally { setBusy(false); }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Loading…</div>;
  if (!b) return <div className="text-sm text-destructive">Booking not found.</div>;

  const stat = statusBadge(b);
  const amountTxt = formatPrice(b.amount_pence, b.currency);
  const paid = b.payment_status === "paid";
  const cutoffIso = b.event?.bookings_close_at || b.event?.starts_at || null;
  const cutoffPassed = cutoffIso ? new Date(cutoffIso).getTime() <= Date.now() : false;
  const attendees = b.attendees || [];
  const isCancelled = (b.status || "").startsWith("cancelled") || b.status === "failed";
  const canCancel = !isCancelled && (b.status === "confirmed" || b.status === "pending_payment");

  return (
    <div>
      <button onClick={() => nav("/bookings")} className="inline-flex items-center gap-1 text-sm text-primary mb-4" data-testid="booking-back">
        <ChevronLeft size={16} /> Back to bookings
      </button>
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl font-semibold" data-testid="booking-event-name">{b.event_name}</h1>
        <Badge variant={stat.variant} data-testid="booking-status-badge">{stat.label}</Badge>
      </div>
      <div className="text-sm text-muted-foreground mt-1">{fmtEventDateTime(b.event?.starts_at)}</div>
      {b.event?.location && (
        <div className="text-sm text-muted-foreground mt-1 inline-flex items-center gap-1"><MapPin size={14} /> {b.event.location}</div>
      )}

      {b.status === "pending_payment" && (checkoutUrl || b.stripe_session_url) && (
        <Card className="p-4 mt-4 border-amber-500/50 bg-amber-50/30">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="font-semibold">Complete your payment</div>
              <div className="text-sm text-muted-foreground">Click below to reopen Stripe Checkout.</div>
            </div>
            <Button onClick={openCheckout} data-testid="booking-resume-payment">Open Stripe Checkout</Button>
          </div>
        </Card>
      )}

      <Card className="p-5 mt-4">
        <div className="flex items-center gap-2 mb-2">
          <Ticket size={16} className="text-primary" />
          <span className="font-semibold">Booking summary</span>
        </div>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <div><dt className="text-muted-foreground">Places</dt><dd className="font-medium">{b.places}</dd></div>
          <div><dt className="text-muted-foreground">Amount</dt><dd className="font-medium" data-testid="booking-amount">{amountTxt}{paid ? " (paid)" : ""}</dd></div>
          <div><dt className="text-muted-foreground">Booking ID</dt><dd className="font-mono text-xs">{b.booking_id}</dd></div>
          <div><dt className="text-muted-foreground">Booked on</dt><dd className="font-medium">{new Date(b.created_at).toLocaleDateString("en-GB")}</dd></div>
        </dl>
      </Card>

      {b.custom_field_values && Object.keys(b.custom_field_values).length > 0 && (
        <Card className="p-5 mt-4">
          <div className="font-semibold mb-2">Additional booking details</div>
          <dl className="text-sm space-y-1">
            {Object.entries(b.custom_field_values).map(([k, v]) => (
              <div key={k} className="flex justify-between gap-2">
                <dt className="text-muted-foreground">{k}</dt><dd>{v || "—"}</dd>
              </div>
            ))}
          </dl>
        </Card>
      )}

      <div className="mt-4 space-y-3">
        {attendees.map((a, i) => (
          <Card key={i} className="p-5">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Attendee {i + 1}</div>
            <div className="font-semibold">{a.name}</div>
            {a.is_complimentary && a.complimentary_code && (
              <Badge variant="secondary" className="mt-2">Complimentary · {a.complimentary_code}</Badge>
            )}
            {a.menu && (a.menu.meal || a.menu.main) && (
              <div className="text-sm mt-2 text-muted-foreground">
                {a.menu.meal && <div>Meal: <span className="text-foreground">{a.menu.meal}</span></div>}
                {a.menu.starter && <div>Starter: <span className="text-foreground">{a.menu.starter}</span></div>}
                {a.menu.main && <div>Main: <span className="text-foreground">{a.menu.main}</span></div>}
                {a.menu.dessert && <div>Dessert: <span className="text-foreground">{a.menu.dessert}</span></div>}
              </div>
            )}
            {a.custom_fields && Object.keys(a.custom_fields).length > 0 && (
              <dl className="text-sm mt-2 space-y-1">
                {Object.entries(a.custom_fields).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-2">
                    <dt className="text-muted-foreground">{k}</dt><dd>{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Card>
        ))}
      </div>

      {canCancel && (
        <Card className="p-5 mt-4">
          <div className="font-semibold text-destructive mb-2">Cancel booking</div>
          {cancelStage === "idle" ? (
            <div className="flex flex-wrap gap-2">
              {cutoffPassed ? (
                <Button variant="destructive" disabled={busy} onClick={() => requestCancel("not_attending")} data-testid="booking-mark-not-attending">
                  {busy ? "Please wait…" : "Mark as not attending"}
                </Button>
              ) : (
                <Button variant="destructive" disabled={busy} onClick={() => requestCancel("cancel")} data-testid="booking-cancel-request">
                  {busy ? "Please wait…" : (paid ? `Cancel & refund ${amountTxt}` : "Cancel booking")}
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground">
                We emailed a 6-digit code to <span className="font-medium text-foreground">{cancelEmail}</span>. Enter it below to confirm.
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <Label>6-digit code</Label>
                  <Input
                    inputMode="numeric" maxLength={6} value={cancelCode}
                    onChange={(e) => setCancelCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-xl tracking-[8px]"
                    data-testid="booking-cancel-code"
                  />
                </div>
                <Button variant="destructive" onClick={submitCancel} disabled={busy} data-testid="booking-cancel-confirm">
                  {busy ? "…" : "Confirm cancellation"}
                </Button>
                <Button variant="ghost" onClick={() => { setCancelStage("idle"); setCancelCode(""); }}>Abandon</Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
